import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';
import { authApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

// Form fields only — token comes from the URL
const FormSchema = z.object({
  displayName: z.string().min(1, 'Full name is required').max(100).trim(),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password must be at most 128 characters'),
});
type FormValues = z.infer<typeof FormSchema>;

export default function SignupCompletePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = searchParams.get('token') ?? '';

  // Verify the token and get the email to display
  const {
    data: tokenData,
    isLoading: verifying,
    error: verifyError,
  } = useQuery({
    queryKey: ['signup-token', token],
    queryFn: () => authApi.verifySignupToken(token),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<FormValues>({ resolver: zodResolver(FormSchema) });

  const mutation = useMutation({
    mutationFn: authApi.completeSignup,
    onSuccess: (data) => {
      queryClient.setQueryData(['auth', 'me'], data);
      void navigate('/capabilities');
    },
    onError: (err: Error) => setError('root', { message: err.message }),
  });

  const onSubmit = (data: FormValues) => {
    mutation.mutate({ ...data, token });
  };

  // ── No token in URL ──────────────────────────────────────────────────────────
  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid link</CardTitle>
            <CardDescription>This registration link is missing a token.</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/signup" className="text-primary underline-offset-4 hover:underline text-sm">
              Start over
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Verifying token ──────────────────────────────────────────────────────────
  if (verifying) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Token invalid or expired ─────────────────────────────────────────────────
  if (verifyError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Link expired or invalid</CardTitle>
            <CardDescription>
              {(verifyError as Error).message}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/signup" className="text-primary underline-offset-4 hover:underline text-sm">
              Request a new registration link
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Registration form ────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">Complete registration</CardTitle>
          <CardDescription>
            Creating account for <strong>{tokenData?.email}</strong>
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            {errors.root && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {errors.root.message}
              </div>
            )}

            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={tokenData?.email ?? ''} disabled className="bg-muted" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayName">Full name</Label>
              <Input
                id="displayName"
                type="text"
                placeholder="Jane Smith"
                autoComplete="name"
                autoFocus
                {...register('displayName')}
              />
              {errors.displayName && (
                <p className="text-sm text-destructive">{errors.displayName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="At least 8 characters"
                autoComplete="new-password"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>
          </CardContent>

          <CardFooter>
            <Button type="submit" className="w-full" disabled={mutation.isPending}>
              {mutation.isPending ? 'Creating account…' : 'Create account'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
